import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Spinner } from "react-bootstrap";
import moment from "moment";
import PrettyContainer from "../../prettyContainer/PrettyContainer";
import { getBlockedIps, blockIp, unblockIp } from "../../../helpers/api";
import "./ipBlocking.css";

export default function IpBlockingPage() {
  const [blocked, setBlocked] = useState([]);
  const [yourIp, setYourIp] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [okMessage, setOkMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ value: "", reason: "" });

  const load = () => {
    setIsLoading(true);
    getBlockedIps()
      .then((data) => {
        setBlocked(data.blocked || []);
        setYourIp(data.yourIp || "");
        setError("");
      })
      .catch((err) => setError(err || "Could not load the blocked IPs"))
      .finally(() => setIsLoading(false));
  };

  useEffect(load, []);

  const handleChange = (field) => (event) => {
    setForm({ ...form, [field]: event.target.value });
    setError("");
    setOkMessage("");
  };

  const handleBlock = (event) => {
    event.preventDefault();
    if (!form.value.trim() || saving) return;

    setSaving(true);
    setError("");
    setOkMessage("");

    blockIp({ value: form.value.trim(), reason: form.reason.trim() })
      .then((created) => {
        setOkMessage(`${created.value} is now blocked`);
        setForm({ value: "", reason: "" });
        load();
      })
      .catch((err) => setError(err || "Could not block that IP"))
      .finally(() => setSaving(false));
  };

  const handleUnblock = (rule) => {
    if (!window.confirm(`Unblock ${rule.value}?`)) return;

    setError("");
    setOkMessage("");
    unblockIp(rule._id)
      .then(() => {
        setOkMessage(`${rule.value} was unblocked`);
        load();
      })
      .catch((err) => setError(err || "Could not unblock that IP"));
  };

  return (
    <PrettyContainer tittle="IP Blocking">
      <div className="ig-ipblock-body">
        <p className="ig-ipblock-intro">
          Anyone connecting from a blocked address gets{" "}
          <b>&ldquo;Your IP has been blocked&rdquo;</b> and cannot sign in.
          {yourIp && (
            <>
              {" "}
              Your current IP is <code className="ig-ipblock-yours">{yourIp}</code>{" "}
              and cannot be blocked from here.
            </>
          )}
        </p>

        <form className="ig-ipblock-form" onSubmit={handleBlock}>
          <div className="ig-ipblock-field">
            <label htmlFor="ig-ipblock-value">IP address or range</label>
            <input
              id="ig-ipblock-value"
              type="text"
              placeholder="190.4.7.22   or   190.4.0.0/16"
              value={form.value}
              onChange={handleChange("value")}
              autoComplete="off"
            />
          </div>

          <div className="ig-ipblock-field ig-ipblock-grow">
            <label htmlFor="ig-ipblock-reason">Reason (optional)</label>
            <input
              id="ig-ipblock-reason"
              type="text"
              placeholder="Sharing the account"
              value={form.reason}
              onChange={handleChange("reason")}
              autoComplete="off"
            />
          </div>

          <Button type="submit" variant="danger" disabled={saving || !form.value.trim()}>
            {saving ? "Blocking…" : "Block"}
          </Button>
        </form>

        {error && <Alert variant="danger" className="ig-ipblock-alert">{error}</Alert>}
        {okMessage && <Alert variant="success" className="ig-ipblock-alert">{okMessage}</Alert>}

        {isLoading ? (
          <div className="ig-ipblock-loading">
            <Spinner animation="border" size="sm" /> Loading…
          </div>
        ) : blocked.length === 0 ? (
          <div className="ig-ipblock-empty">No IP is blocked right now.</div>
        ) : (
          <div className="ig-ipblock-tablewrap">
            <table className="ig-ipblock-table">
              <thead>
                <tr>
                  <th>Address</th>
                  <th>Reason</th>
                  <th>Blocked attempts</th>
                  <th>Last attempt</th>
                  <th>Blocked by</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {blocked.map((rule) => (
                  <tr key={rule._id}>
                    <td>
                      <code className="ig-ipblock-value">{rule.value}</code>{" "}
                      {rule.kind === "cidr" && <Badge bg="secondary">range</Badge>}
                    </td>
                    <td>{rule.reason || <span className="ig-ipblock-muted">—</span>}</td>
                    <td>{rule.hits || 0}</td>
                    <td>
                      {rule.lastHit ? (
                        moment(rule.lastHit).format("MMM D, YYYY HH:mm")
                      ) : (
                        <span className="ig-ipblock-muted">—</span>
                      )}
                    </td>
                    <td>
                      <div>{rule.createdByName || "—"}</div>
                      <div className="ig-ipblock-muted">
                        {moment(rule.createdAt).format("MMM D, YYYY")}
                      </div>
                    </td>
                    <td className="ig-ipblock-actions">
                      <Button size="sm" variant="outline-secondary" onClick={() => handleUnblock(rule)}>
                        Unblock
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PrettyContainer>
  );
}
